import React, { useRef } from 'react';
import { IconPencil, IconEye, IconSend, IconCalendarEvent } from '@tabler/icons-react';
import { Anchor, Glow, Pill, Wires, Sparks, gsap, travel, flare, blink, ambience, entrance, useSceneAnimation } from '../../../../../../components/Scene';
import './styles.scss';

// January 2027, the launch month, from Sunday the 3rd. Each stage sends its
// content up into a day; the badge and gauge follow along.
const FIRST_DAY = Date.UTC(2027, 0, 3);
const day = (row, col) => new Date(FIRST_DAY + (row * 7 + col) * 86400000);
const COL = col => 42 + 276 / 7 * (col + 0.5);
const ROW_TOP = row => 130 + row * 40;
const STAGES = [
  { id: 'create', label: 'artCreate', tone: 'warm', icon: IconPencil, x: 70, cell: [2, 1], d: 'M70 339 V328 Q70 320 78 320 H93 Q101 320 101 312 V300' },
  { id: 'review', label: 'artReview', tone: 'violet', icon: IconEye, x: 180, cell: [1, 3], d: 'M180 339 V300' },
  { id: 'publish', label: 'artPublish', tone: 'cool', icon: IconSend, x: 290, cell: [3, 5], d: 'M290 339 V328 Q290 320 282 320 H267 Q259 320 259 312 V300' },
];
const WIRES = STAGES.map(({ id, tone, x, cell, d }) => ({ id, tone, d, joints: [[x, 339], [COL(cell[1]), 300]] }));
// Earlier plans already on the calendar, drawn dim.
const PLANNED = [[0, 0, 'cool'], [0, 4, 'warm'], [1, 5, 'violet'], [2, 6, 'cool'], [3, 2, 'warm'], [2, 3, 'violet']];
const LAUNCH = [0, 2];
const SPARKS = [[120, 64], [244, 58], [300, 96], [64, 112]];
const STEP = 2.4;

function build(q, { still }) {
  const events = q('[data-event]');
  const badges = q('[data-badge-step]');
  const arc = q('[data-gauge]');
  const count = q('[data-gauge-count]')[0];
  if (still) return;
  ambience(q);
  gsap.to(q('[data-launch]'), { opacity: 0.35, duration: 1.2, ease: 'sine.inOut', yoyo: true, repeat: -1 });
  const intro = entrance(q);
  intro.from(q('[data-planned]'), { scaleX: 0, transformOrigin: 'left center', duration: 0.4, stagger: 0.05, ease: 'power2.out' }, 0.5);
  const reset = () => {
    gsap.set(events, { opacity: 0, scaleX: 0, transformOrigin: 'left center' });
    gsap.set(badges, { opacity: 0 });
    gsap.set(arc, { strokeDashoffset: 1 });
    count.textContent = `0/${STAGES.length}`;
  };
  reset();
  const loop = gsap.timeline({ repeat: -1, repeatDelay: 0.3, delay: intro.duration() - 0.2, onRepeat: reset });
  STAGES.forEach(({ id }, i) => {
    const at = i * STEP;
    flare(loop, q, id, at);
    blink(loop, q, [`${id}-0`], at + 0.1);
    travel(loop, q, id, at + 0.12, 0.6);
    blink(loop, q, [`${id}-1`], at + 0.7);
    loop.fromTo(q(`[data-column="${i}"]`), { opacity: 1, scaleY: 0, transformOrigin: 'center bottom' }, { scaleY: 1, duration: 0.35, ease: 'power2.out' }, at + 0.72)
      .to(q(`[data-column="${i}"]`), { opacity: 0, duration: 0.6 }, at + 1.07)
      .to(q(`[data-event="${i}"]`), { opacity: 1, scaleX: 1, duration: 0.45, ease: 'power3.out' }, at + 1)
      .fromTo(q(`[data-cell-ring="${i}"]`), { opacity: 0 }, { opacity: 1, duration: 0.2, yoyo: true, repeat: 1, repeatDelay: 0.4 }, at + 1)
      .to(badges.filter((_, n) => n !== i), { opacity: 0, y: -4, duration: 0.25 }, at + 0.95)
      .fromTo(badges[i], { opacity: 0, y: 6 }, { opacity: 1, y: 0, duration: 0.4, ease: 'power3.out' }, at + 1.05)
      .to(arc, { strokeDashoffset: 1 - (i + 1) / STAGES.length, duration: 0.5, ease: 'power2.out' }, at + 1.05)
      .call(() => { count.textContent = `${i + 1}/${STAGES.length}`; }, null, at + 1.1);
  });
  loop.to(events, { opacity: 0, duration: 0.5 }, '+=1.2').to(badges, { opacity: 0, duration: 0.4 }, '<');
}

export default function CalendarScene({ t, locale }) {
  const stage = useRef(null);
  useSceneAnimation(stage, build);
  const month = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(FIRST_DAY);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'narrow', timeZone: 'UTC' });
  const weekdayShort = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  const eventAt = (row, col) => STAGES.findIndex(item => item.cell[0] === row && item.cell[1] === col);
  const plannedAt = (row, col) => PLANNED.find(([r, c]) => r === row && c === col);
  return <div className="studio-feature-art__stage" ref={stage}>
    <Glow x={180} y={186} tone="violet" size={230} />
    <Glow x={76} y={340} tone="warm" size={120} />
    <Glow x={300} y={110} tone="cool" size={130} />
    <Sparks points={SPARKS} />
    <Wires wires={WIRES} />
    <Anchor x={180} y={44}>
      <span className="studio-feature-art__badges">
        {STAGES.map(({ id, label, tone, cell }, i) => <span key={id} className={`studio-feature-art__pill studio-feature-art__plan is-${tone}`} data-badge-step style={{ opacity: i === STAGES.length - 1 ? 1 : 0 }}>
          <i className="studio-feature-art__plan-dot" /><IconCalendarEvent aria-hidden="true" />
          {t(label)} · {weekdayShort.format(day(...cell))} {day(...cell).getUTCDate()}
        </span>)}
      </span>
    </Anchor>
    <Anchor x={180} y={186}>
      <div className="studio-feature-art__card studio-feature-art__calendar" data-enter>
        <div className="studio-feature-art__calendar-head">
          <span>{month.charAt(0).toUpperCase() + month.slice(1)}</span>
          <span className="studio-feature-art__gauge">
            <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" pathLength="1" /><circle cx="12" cy="12" r="9" pathLength="1" data-gauge /></svg>
            <b data-gauge-count>{STAGES.length}/{STAGES.length}</b>
          </span>
        </div>
        <div className="studio-feature-art__weekdays">{Array.from({ length: 7 }, (_, col) => <span key={col}>{weekday.format(day(0, col))}</span>)}</div>
        <div className="studio-feature-art__days">
          {Array.from({ length: 28 }, (_, n) => {
            const row = Math.floor(n / 7);
            const col = n % 7;
            const event = eventAt(row, col);
            const planned = plannedAt(row, col);
            const launch = row === LAUNCH[0] && col === LAUNCH[1];
            return <span key={n} className={`studio-feature-art__day ${launch ? 'is-launch' : ''}`}>
              {launch && <i className="studio-feature-art__launch" data-launch />}
              {event >= 0 && <i className={`studio-feature-art__cell-ring is-${STAGES[event].tone}`} data-cell-ring={event} />}
              <small>{day(row, col).getUTCDate()}</small>
              {planned && <i className={`studio-feature-art__event is-planned is-${planned[2]}`} data-planned />}
              {event >= 0 && <i className={`studio-feature-art__event is-${STAGES[event].tone}`} data-event={event} />}
            </span>;
          })}
        </div>
      </div>
    </Anchor>
    {STAGES.map(({ id, tone, cell }, i) => <span key={id} className={`studio-feature-art__column is-${tone}`} data-column={i}
      style={{ left: `${(COL(cell[1]) - 276 / 14) / 3.6}%`, width: `${276 / 7 / 3.6}%`, top: `${ROW_TOP(cell[0]) / 4}%`, height: `${(300 - ROW_TOP(cell[0])) / 4}%` }} />)}
    {STAGES.map(({ id, label, tone, icon, x }) => <Anchor key={id} x={x} y={352}><Pill id={id} tone={tone} icon={icon}>{t(label)}</Pill></Anchor>)}
  </div>;
}
